// =====================================================================
// PLENARIUM:CENTRAL_DOCUMENTOS -- Central de Documentos
//  modo="admin": todos os documentos, com a liberacao para os vereadores
//  modo="vereador": so os documentos liberados pelo admin
// Organizada por tipo (requerimento, indicacao, parecer, emenda...), com
// filtros: texto, tipo, autor, periodo, ano, situacao e materia vinculada.
// Visualizar (abre o documento) e Baixar.
// =====================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileText, Download, Eye, Search, X, Check, Lock, Unlock } from 'lucide-react';
import { abrirDocumento } from './TodosProjetos';

const TIPOS = {
  PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Projeto de Resolução', PROJETO_DECRETO: 'Projeto de Decreto Legislativo',
  REQUERIMENTO: 'Requerimento', INDICACAO: 'Indicação', MOCAO: 'Moção', ATA_ANTERIOR: 'Ata', OUTROS: 'Outras matérias',
  ANEXO_PARECER_COMISSAO: 'Parecer de Comissão', ANEXO_PARECER_RELATOR: 'Parecer do Relator', ANEXO_PARECER_JURIDICO: 'Parecer Jurídico',
  ANEXO_EMENDA: 'Emenda', ANEXO_SUBSTITUTIVO: 'Substitutivo', ANEXO_REDACAO_FINAL: 'Redação Final', ANEXO_OFICIO: 'Ofício',
  ANEXO_PROJETO: 'Texto do Projeto', ANEXO_OUTROS: 'Outros documentos'
};
const ORDEM_TIPOS = Object.keys(TIPOS);
const COR = {
  PROJETO_LEI: '#dc2626', PROJETO_RESOLUCAO: '#b45309', PROJETO_DECRETO: '#7c3aed', REQUERIMENTO: '#16a34a', INDICACAO: '#2563eb', MOCAO: '#db2777',
  ATA_ANTERIOR: '#0f766e', ANEXO_PARECER_COMISSAO: '#0891b2', ANEXO_PARECER_RELATOR: '#0e7490', ANEXO_PARECER_JURIDICO: '#155e75', ANEXO_EMENDA: '#ca8a04'
};
const STATUS = { EM_TRAMITACAO: 'Em tramitação', APROVADO: 'Aprovado', REPROVADO: 'Reprovado', ARQUIVADO: 'Arquivado', DEVOLVIDO: 'Devolvido', PROMULGADO: 'Promulgado' };
const rotTipo = (t) => TIPOS[t] || t;
const corTipo = (t) => COR[t] || '#475569';
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const dataBR = (d) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch (e) { return ''; } };
const diaISO = (d) => { try { const x = new Date(d); return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10); } catch (e) { return ''; } };
const materiaTxt = (m) => (m ? `${TIPOS[m.tipo] || m.tipo} nº ${m.numero}/${m.ano}` : '');
const POR_PAGINA = 40;

const CSS = `
.pcd { font-family: inherit; color: #0f172a; }
.pcd-topo { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.pcd-topo h2 { font-size: 1.15rem; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 8px; }
.pcd-topo small { color: #64748b; font-size: .82rem; }
.pcd-tipos { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.pcd-chip { border: 1px solid #e2e8f0; background: #fff; border-radius: 999px; padding: 6px 12px; font: inherit; font-size: .82rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; color: #0f172a; }
.pcd-chip i { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.pcd-chip b { color: #64748b; font-weight: 700; }
.pcd-chip.on { border-color: #1d4ed8; background: #eff6ff; }
.pcd-filtros { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; margin-bottom: 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px; }
.pcd-filtros label { display: flex; flex-direction: column; gap: 3px; font-size: .72rem; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .03em; }
.pcd-filtros input, .pcd-filtros select { font: inherit; font-size: .88rem; text-transform: none; letter-spacing: 0; font-weight: 500; color: #0f172a; border: 1px solid #cbd5e1; border-radius: 8px; padding: 7px 9px; background: #fff; min-width: 0; }
.pcd-busca { grid-column: 1 / -1; position: relative; }
.pcd-busca input { padding-left: 32px; width: 100%; box-sizing: border-box; }
.pcd-busca svg { position: absolute; left: 10px; bottom: 9px; color: #94a3b8; }
.pcd-barra { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; font-size: .85rem; color: #475569; }
.pcd-bt { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #cbd5e1; background: #fff; border-radius: 9px; padding: 7px 12px; font: inherit; font-size: .84rem; font-weight: 700; cursor: pointer; color: #0f172a; }
.pcd-bt:disabled { opacity: .55; cursor: default; }
.pcd-bt.azul { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.pcd-bt.verde { background: #16a34a; border-color: #16a34a; color: #fff; }
.pcd-bt.verm { color: #b91c1c; border-color: #fca5a5; }
.pcd-lista { display: flex; flex-direction: column; gap: 8px; }
.pcd-item { display: flex; gap: 12px; align-items: flex-start; background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #94a3b8; border-radius: 12px; padding: 11px 13px; }
.pcd-item input[type=checkbox] { width: 18px; height: 18px; margin-top: 3px; flex-shrink: 0; }
.pcd-corpo { flex: 1; min-width: 0; }
.pcd-tag { display: inline-block; font-size: .7rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; border-radius: 6px; padding: 2px 7px; color: #fff; margin-right: 6px; }
.pcd-tit { font-weight: 700; font-size: .95rem; margin: 4px 0 2px; word-break: break-word; }
.pcd-meta { font-size: .78rem; color: #64748b; display: flex; gap: 10px; flex-wrap: wrap; }
.pcd-link { color: #1d4ed8; cursor: pointer; text-decoration: underline; background: none; border: 0; padding: 0; font: inherit; }
.pcd-acoes { display: flex; gap: 6px; flex-wrap: wrap; flex-shrink: 0; align-items: center; }
.pcd-lib { font-size: .72rem; font-weight: 800; border-radius: 999px; padding: 3px 9px; white-space: nowrap; }
.pcd-lib.sim { background: #dcfce7; color: #15803d; } .pcd-lib.nao { background: #f1f5f9; color: #64748b; }
.pcd-vazio { text-align: center; color: #64748b; padding: 30px 12px; background: #f8fafc; border-radius: 12px; }
@media (max-width: 640px) { .pcd-item { flex-wrap: wrap; } .pcd-acoes { width: 100%; } }
`;

async function baixar(chave, nomeBase) {
  try {
    const r = await axios.get(`/api/central-documentos/arquivo/${encodeURIComponent(chave)}`);
    const dados = r.data && r.data.arquivo;
    if (!dados) { toast.error('Documento sem arquivo.'); return; }
    let url = dados; let ext = '';
    if (dados.startsWith('data:')) {
      const [cab, corpo] = dados.split(',');
      const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/pdf';
      ext = tipo.includes('pdf') ? '.pdf' : tipo.includes('word') ? '.docx' : tipo.includes('png') ? '.png' : tipo.includes('jpeg') ? '.jpg' : '';
      const bin = atob(corpo); const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      url = URL.createObjectURL(new Blob([bytes], { type: tipo }));
    }
    const nome = String((r.data && r.data.nome) || nomeBase || 'documento').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 120);
    const a = document.createElement('a');
    a.href = url; a.download = /\.[a-z0-9]{2,5}$/i.test(nome) ? nome : nome + ext;
    document.body.appendChild(a); a.click(); a.remove();
    if (url.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error((e && e.response && e.response.data && e.response.data.error) || 'Não foi possível baixar o documento.');
  }
}

const FILTRO0 = { busca: '', tipo: '', autor: '', de: '', ate: '', ano: '', status: '', materia: '', lib: '' };

export default function CentralDocumentos({ modo = 'admin' }) {
  const admin = modo === 'admin';
  const [dados, setDados] = useState(null);
  const [f, setF] = useState(FILTRO0);
  const [qtd, setQtd] = useState(POR_PAGINA);
  const [sel, setSel] = useState(() => new Set());
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try { const r = await axios.get('/api/central-documentos'); setDados(r.data || { documentos: [] }); }
    catch (e) { setDados({ documentos: [], erro: (e.response && e.response.data && e.response.data.error) || 'Não foi possível carregar os documentos.' }); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setQtd(POR_PAGINA); }, [f]);

  const docs = useMemo(() => (dados && dados.documentos) || [], [dados]);
  const autores = useMemo(() => [...new Set(docs.flatMap(d => [d.autor, d.materia && d.materia.autor]).filter(Boolean).map(s => String(s).trim()))].sort((a, b) => a.localeCompare(b, 'pt-BR')), [docs]);
  const anos = useMemo(() => [...new Set(docs.map(d => d.ano).filter(Boolean))].sort((a, b) => b - a), [docs]);
  const materias = useMemo(() => {
    const m = new Map(); docs.forEach(d => { if (d.materia && !m.has(d.materia.id)) m.set(d.materia.id, d.materia); });
    return [...m.values()].sort((a, b) => (b.ano - a.ano) || String(a.tipo).localeCompare(String(b.tipo)) || String(b.numero).localeCompare(String(a.numero), 'pt-BR', { numeric: true }));
  }, [docs]);

  const filtrados = useMemo(() => {
    const q = semAcento(f.busca.trim());
    return docs.filter(d => {
      if (f.tipo && d.tipo !== f.tipo) return false;
      if (f.autor && d.autor !== f.autor && !(d.materia && d.materia.autor === f.autor)) return false;
      if (f.ano && String(d.ano) !== f.ano) return false;
      if (f.status && d.status !== f.status) return false;
      if (f.materia && !(d.materia && d.materia.id === f.materia)) return false;
      if (f.lib === 'sim' && !d.liberado) return false;
      if (f.lib === 'nao' && d.liberado) return false;
      const dia = diaISO(d.data);
      if (f.de && dia < f.de) return false;
      if (f.ate && dia > f.ate) return false;
      if (q) {
        const alvo = semAcento([d.titulo, d.descricao, d.autor, rotTipo(d.tipo), d.materia && materiaTxt(d.materia), d.materia && d.materia.ementa, d.materia && d.materia.autor].join(' '));
        if (!q.split(/\s+/).every(p => alvo.includes(p))) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.data) - new Date(a.data));
  }, [docs, f]);

  const contagemTipo = useMemo(() => { const c = {}; docs.forEach(d => { c[d.tipo] = (c[d.tipo] || 0) + 1; }); return c; }, [docs]);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const temFiltro = Object.keys(FILTRO0).some(k => f[k]);

  const liberar = async (chaves, liberado) => {
    if (!chaves.length) return;
    setOcupado(true);
    try {
      await axios.put('/api/central-documentos/liberar', { chaves, liberado });
      toast.success(liberado ? `${chaves.length} documento(s) liberado(s) para os vereadores.` : `${chaves.length} documento(s) retirado(s) da visão dos vereadores.`);
      setSel(new Set()); await carregar();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível atualizar.'); }
    finally { setOcupado(false); }
  };
  const alternarSel = (chave) => setSel(s => { const n = new Set(s); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });

  if (!dados) return <div className="pcd"><style>{CSS}</style><div className="pcd-vazio">Carregando…</div></div>;
  const visiveis = filtrados.slice(0, qtd);
  const tiposPresentes = ORDEM_TIPOS.filter(t => contagemTipo[t]).concat(Object.keys(contagemTipo).filter(t => !TIPOS[t]));

  return (
    <div className="pcd">
      <style>{CSS}</style>
      <div className="pcd-topo">
        <div>
          <h2><FileText size={20} /> Central de Documentos</h2>
          <small>{admin
            ? `${dados.total || docs.length} documento(s) do Trâmite · ${dados.liberados || 0} liberado(s) para os vereadores`
            : `${docs.length} documento(s) disponível(is)`}</small>
        </div>
        {temFiltro && <button type="button" className="pcd-bt" onClick={() => setF(FILTRO0)}><X size={15} /> Limpar filtros</button>}
      </div>
      {dados.erro && <div className="pcd-vazio" style={{ color: '#b91c1c' }}>{dados.erro}</div>}

      {/* organizada por tipo */}
      <div className="pcd-tipos">
        <button type="button" className={`pcd-chip ${!f.tipo ? 'on' : ''}`} onClick={() => set('tipo', '')}>Todos <b>{docs.length}</b></button>
        {tiposPresentes.map(t => (
          <button key={t} type="button" className={`pcd-chip ${f.tipo === t ? 'on' : ''}`} onClick={() => set('tipo', f.tipo === t ? '' : t)}>
            <i style={{ background: corTipo(t) }} />{rotTipo(t)} <b>{contagemTipo[t]}</b>
          </button>
        ))}
      </div>

      <div className="pcd-filtros">
        <label className="pcd-busca">Buscar
          <Search size={15} />
          <input value={f.busca} onChange={e => set('busca', e.target.value)} placeholder="Número, título, ementa, descrição ou autor" />
        </label>
        <label>Tipo
          <select value={f.tipo} onChange={e => set('tipo', e.target.value)}>
            <option value="">Todos</option>
            {tiposPresentes.map(t => <option key={t} value={t}>{rotTipo(t)}</option>)}
          </select>
        </label>
        <label>Autor
          <select value={f.autor} onChange={e => set('autor', e.target.value)}>
            <option value="">Todos</option>
            {autores.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label>De
          <input type="date" value={f.de} onChange={e => set('de', e.target.value)} />
        </label>
        <label>Até
          <input type="date" value={f.ate} onChange={e => set('ate', e.target.value)} />
        </label>
        <label>Ano
          <select value={f.ano} onChange={e => set('ano', e.target.value)}>
            <option value="">Todos</option>
            {anos.map(a => <option key={a} value={String(a)}>{a}</option>)}
          </select>
        </label>
        <label>Situação
          <select value={f.status} onChange={e => set('status', e.target.value)}>
            <option value="">Todas</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label style={{ gridColumn: 'span 2' }}>Matéria vinculada
          <select value={f.materia} onChange={e => set('materia', e.target.value)}>
            <option value="">Todas</option>
            {materias.map(m => <option key={m.id} value={m.id}>{materiaTxt(m)} — {String(m.ementa || '').slice(0, 70)}</option>)}
          </select>
        </label>
        {admin && (
          <label>Vereadores
            <select value={f.lib} onChange={e => set('lib', e.target.value)}>
              <option value="">Todos</option>
              <option value="sim">Liberados</option>
              <option value="nao">Não liberados</option>
            </select>
          </label>
        )}
      </div>

      <div className="pcd-barra">
        <span><b>{filtrados.length}</b> documento(s) encontrado(s)</span>
        {admin && filtrados.length > 0 && (
          <>
            <span style={{ flex: 1 }} />
            {sel.size > 0 && <span><b>{sel.size}</b> marcado(s)</span>}
            <button type="button" className="pcd-bt" disabled={ocupado} onClick={() => setSel(s => (s.size === filtrados.length ? new Set() : new Set(filtrados.map(d => d.chave))))}>{sel.size === filtrados.length ? 'Desmarcar todos' : 'Marcar todos os filtrados'}</button>
            <button type="button" className="pcd-bt verde" disabled={ocupado || !sel.size} onClick={() => liberar([...sel], true)}><Unlock size={14} /> Liberar para vereadores</button>
            <button type="button" className="pcd-bt verm" disabled={ocupado || !sel.size} onClick={() => liberar([...sel], false)}><Lock size={14} /> Retirar</button>
          </>
        )}
      </div>

      {!filtrados.length ? (
        <div className="pcd-vazio">{docs.length ? 'Nenhum documento com esses filtros.' : admin ? 'Nenhum documento no Trâmite ainda. Os arquivos das matérias e os anexos (pareceres, emendas...) aparecem aqui sozinhos.' : 'Nenhum documento liberado pela Câmara até agora.'}</div>
      ) : (
        <div className="pcd-lista">
          {visiveis.map(d => (
            <div key={d.chave} className="pcd-item" style={{ borderLeftColor: corTipo(d.tipo) }}>
              {admin && <input type="checkbox" checked={sel.has(d.chave)} onChange={() => alternarSel(d.chave)} aria-label="Marcar documento" />}
              <div className="pcd-corpo">
                <span className="pcd-tag" style={{ background: corTipo(d.tipo) }}>{rotTipo(d.tipo)}</span>
                {d.origem === 'MATERIA' && d.materia && <b style={{ fontSize: '.85rem' }}>nº {d.materia.numero}/{d.materia.ano}</b>}
                <p className="pcd-tit">{d.titulo}</p>
                {d.descricao && d.descricao !== d.titulo && <p style={{ fontSize: '.82rem', color: '#475569', margin: '0 0 4px' }}>{d.descricao}</p>}
                <div className="pcd-meta">
                  {(d.autor || (d.materia && d.materia.autor)) && <span>Autor: {d.autor || d.materia.autor}</span>}
                  <span>{dataBR(d.data)}</span>
                  {d.status && <span>{STATUS[d.status] || d.status}</span>}
                  {d.origem === 'ANEXO' && d.materia && <span>Matéria: <button type="button" className="pcd-link" onClick={() => set('materia', d.materia.id)}>{materiaTxt(d.materia)}</button></span>}
                  {d.origem === 'MATERIA' && d.materia && <span><button type="button" className="pcd-link" onClick={() => set('materia', d.materia.id)}>todos os documentos desta matéria</button></span>}
                </div>
              </div>
              <div className="pcd-acoes">
                {admin && <span className={`pcd-lib ${d.liberado ? 'sim' : 'nao'}`}>{d.liberado ? 'Liberado' : 'Só admin'}</span>}
                <button type="button" className="pcd-bt" onClick={() => abrirDocumento(`/api/central-documentos/arquivo/${encodeURIComponent(d.chave)}`)}><Eye size={15} /> Visualizar</button>
                <button type="button" className="pcd-bt azul" onClick={() => baixar(d.chave, `${rotTipo(d.tipo)} ${d.materia ? d.materia.numero + '-' + d.materia.ano : ''}`)}><Download size={15} /> Baixar</button>
                {admin && (d.liberado
                  ? <button type="button" className="pcd-bt verm" disabled={ocupado} onClick={() => liberar([d.chave], false)} title="Retirar da visão dos vereadores"><Lock size={14} /></button>
                  : <button type="button" className="pcd-bt verde" disabled={ocupado} onClick={() => liberar([d.chave], true)} title="Liberar para os vereadores"><Check size={14} /></button>)}
              </div>
            </div>
          ))}
          {filtrados.length > visiveis.length && (
            <button type="button" className="pcd-bt" style={{ alignSelf: 'center', marginTop: 6 }} onClick={() => setQtd(q => q + POR_PAGINA)}>Mostrar mais ({filtrados.length - visiveis.length} restante(s))</button>
          )}
        </div>
      )}
    </div>
  );
}
